/**
 * MSAL Provider Context
 * 
 * Wraps the application with MSAL authentication context and handles
 * the SSO redirect flow automatically.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react'
import {
  MsalProvider as MsalReactProvider,
  useMsal,
  useIsAuthenticated,
} from '@azure/msal-react'
import {
  PublicClientApplication,
  EventType,
  EventMessage,
  AuthenticationResult,
  InteractionStatus,
} from '@azure/msal-browser'
import { useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box, CircularProgress, Typography, Alert } from '@mui/material'

import { msalConfig, loginRequest, isAzureSSOConfigured, API_BRIDGE_ENDPOINT } from '../config/authConfig'
import logger from '../utils/logger'
import { setToken, clearAuth } from '../features/auth/authSlice'
import api from '../api/client'
import type { AppDispatch } from '../app/store'

// Create MSAL instance - initialization happens in MsalAuthProvider
let msalInstance: PublicClientApplication | null = null

if (isAzureSSOConfigured()) {
  msalInstance = new PublicClientApplication(msalConfig)
}

/**
 * Bridge response from backend
 */
interface BridgeResponse {
  created: boolean
  user: {
    email: string
    display_name: string
    emp_id: string | null
  }
}

// Store the redirect result to pass to AuthenticationHandler
let pendingRedirectResult: AuthenticationResult | null = null

/**
 * Component that handles the SSO authentication flow
 */
const AuthenticationHandler: React.FC<{ children: React.ReactNode; redirectResult: AuthenticationResult | null }> = ({ children, redirectResult }) => {
  const { instance, accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const location = useLocation()
  const [error, setError] = useState<string | null>(null)
  const [isExchangingToken, setIsExchangingToken] = useState(false)
  const hasProcessedRedirect = useRef(false)
  const hasCheckedExistingSession = useRef(false)

  /**
   * Exchange the Azure AD id_token with our backend to establish a session
   */
  const exchangeTokenWithBackend = useCallback(async (idToken: string, shouldNavigate: boolean = true) => {
    if (!idToken) {
      setError('No ID token received from Azure AD')
      return false
    }

    setIsExchangingToken(true)
    setError(null)

    try {
      logger.info('[SSO] Exchanging token with backend...')
      // Call our backend bridge endpoint
      const response = await api.post<BridgeResponse>(API_BRIDGE_ENDPOINT, {
        id_token: idToken,
      })

      logger.debug('[SSO] Backend bridge success:', response.data)

      // Backend sets session cookie automatically
      // Store minimal user info for the frontend
      const user = response.data.user
      
      // For SSO, we use session-based auth, but we can still track authentication state
      // Set a marker token (the actual auth is via session cookie)
      dispatch(setToken('sso-session'))
      
      // Store user info in localStorage for display purposes
      localStorage.setItem('user', JSON.stringify({
        email: user.email,
        first_name: user.display_name.split(' ')[0] || '',
        last_name: user.display_name.split(' ').slice(1).join(' ') || '',
        display_name: user.display_name,
        emp_id: user.emp_id,
      }))

      // Navigate to dashboard or the originally requested page
      if (shouldNavigate) {
        const returnTo = location.state?.from?.pathname || '/dashboard'
        logger.debug('[SSO] Navigating to:', returnTo)
        navigate(returnTo, { replace: true })
      }
      return true
    } catch (err: any) {
      logger.error('[SSO] Backend bridge error:', err)
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to authenticate with server'
      setError(errorMessage)
      
      // Clear any partial auth state
      dispatch(clearAuth())
      return false
    } finally {
      setIsExchangingToken(false)
    }
  }, [dispatch, navigate, location.state])

  // Process the redirect result passed from parent (captured during initialization)
  useEffect(() => {
    if (redirectResult && !hasProcessedRedirect.current) {
      hasProcessedRedirect.current = true
      logger.debug('[SSO] Processing redirect result from initialization')
      exchangeTokenWithBackend(redirectResult.idToken, true)
    }
  }, [redirectResult, exchangeTokenWithBackend])

  // Check for existing MSAL session and establish backend session if needed
  // This handles the case where user has cached MSAL tokens but page was refreshed
  useEffect(() => {
    const checkExistingSession = async () => {
      // Skip if already processed redirect, already checking, or no accounts
      if (hasProcessedRedirect.current || hasCheckedExistingSession.current || accounts.length === 0) {
        return
      }

      // Check if we already have a backend session (token in Redux/localStorage)
      const existingToken = localStorage.getItem('token')
      if (existingToken) {
        logger.debug('[SSO] Existing backend session found, skipping token exchange')
        // Make sure Redux state is synced
        dispatch(setToken(existingToken))
        return
      }

      hasCheckedExistingSession.current = true
      logger.debug('[SSO] Found existing MSAL account, acquiring token silently...')

      try {
        // Try to get a fresh token silently
        const silentRequest = {
          scopes: ['openid', 'profile', 'email'],
          account: accounts[0],
        }
        
        const response = await instance.acquireTokenSilent(silentRequest)
        logger.debug('[SSO] Silent token acquired, exchanging with backend...')
        
        // Exchange with backend - don't navigate, stay on current page
        const success = await exchangeTokenWithBackend(response.idToken, false)
        if (success && location.pathname === '/login') {
          navigate('/dashboard', { replace: true })
        }
      } catch (err: any) {
        logger.error('[SSO] Silent token acquisition failed:', err)
        // If silent acquisition fails, user needs to login again
        // Clear the stale MSAL cache
        if (err.name === 'InteractionRequiredAuthError') {
          logger.debug('[SSO] Interaction required, user needs to login again')
        }
      }
    }

    // Only check after MSAL is done with any interactions
    if (inProgress === InteractionStatus.None) {
      checkExistingSession()
    }
  }, [accounts, instance, inProgress, dispatch, navigate, location.pathname, exchangeTokenWithBackend])

  // Listen for MSAL events (for popup flow or other scenarios)
  useEffect(() => {
    const callbackId = instance.addEventCallback((event: EventMessage) => {
      logger.debug('[MSAL] Event:', event.eventType)
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        const result = event.payload as AuthenticationResult
        if (result && !hasProcessedRedirect.current) {
          hasProcessedRedirect.current = true
          exchangeTokenWithBackend(result.idToken, true)
        }
      } else if (event.eventType === EventType.LOGIN_FAILURE) {
        logger.error('[SSO] Login failed:', event.error)
        setError(event.error?.message || 'Login failed')
      } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
        dispatch(clearAuth())
        navigate('/login')
      }
    })

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId)
      }
    }
  }, [instance, dispatch, navigate, exchangeTokenWithBackend])

  // Show loading state while handling redirect or exchanging token
  if (inProgress !== InteractionStatus.None || isExchangingToken) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          {isExchangingToken ? 'Completing sign in...' : 'Authenticating...'}
        </Typography>
      </Box>
    )
  }

  // Show error if authentication failed
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Alert 
          severity="error" 
          sx={{ mb: 2, maxWidth: 500 }}
          action={
            <button 
              onClick={() => {
                setError(null)
                navigate('/login')
              }}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: 'inherit', 
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Try again
            </button>
          }
        >
          {error}
        </Alert>
      </Box>
    )
  }

  return <>{children}</>
}

/**
 * MSAL Provider wrapper component
 * Only initializes MSAL if Azure SSO is configured
 */
export const MsalAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false)
  const [initError, setInitError] = useState<string | null>(null)
  const [redirectResult, setRedirectResult] = useState<AuthenticationResult | null>(null)

  useEffect(() => {
    const initializeMsal = async () => {
      if (!msalInstance) {
        setIsInitialized(true)
        return
      }

      try {
        // MSAL v3+ requires explicit initialization before any other calls
        await msalInstance.initialize()
        logger.debug('[MSAL] Initialized successfully')
        
        // Handle redirect promise IMMEDIATELY after initialization
        // This is critical - the hash fragment with auth response is only available once
        try {
          const response = await msalInstance.handleRedirectPromise()
          if (response) {
            logger.debug('[MSAL] Redirect response captured:', response.account?.username)
            setRedirectResult(response)
          } else {
            logger.debug('[MSAL] No redirect response (not coming from auth redirect)')
          }
        } catch (redirectErr: any) {
          logger.error('[MSAL] Redirect handling error:', redirectErr)
          setInitError(redirectErr.message || 'Authentication redirect failed')
        }
        
        setIsInitialized(true)
      } catch (err: any) {
        logger.error('[MSAL] Initialization error:', err)
        setInitError(err.message || 'Failed to initialize authentication')
        setIsInitialized(true) // Allow fallback rendering
      }
    }

    initializeMsal()
  }, [])

  if (!isAzureSSOConfigured() || !msalInstance) {
    // SSO not configured, render children without MSAL
    // This allows fallback to traditional login during development
    return <>{children}</>
  }

  // Show loading while MSAL initializes
  if (!isInitialized) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Initializing...
        </Typography>
      </Box>
    )
  }

  // Show error if initialization failed
  if (initError) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ mb: 2, maxWidth: 500 }}>
          {initError}
        </Alert>
      </Box>
    )
  }

  return (
    <MsalReactProvider instance={msalInstance}>
      <AuthenticationHandler redirectResult={redirectResult}>{children}</AuthenticationHandler>
    </MsalReactProvider>
  )
}

/**
 * Hook to get the MSAL instance (only after initialization)
 * Returns null if SSO is not configured or not yet initialized
 */
export const useMsalInstance = () => {
  if (!isAzureSSOConfigured()) {
    return null
  }
  return msalInstance
}

/**
 * Hook to initiate SSO login
 * Uses useMsal() hook to ensure we're within the initialized MsalProvider context
 */
export const useSSOLogin = () => {
  const { instance, inProgress } = useMsal()

  const login = async () => {
    if (!instance) {
      throw new Error('Azure SSO is not configured')
    }

    // Prevent calling interactive APIs while another interaction is in progress
    if (inProgress && inProgress !== InteractionStatus.None) {
      throw new Error('An authentication interaction is already in progress. Please wait and try again.')
    }

    // Use redirect flow for better UX
    await instance.loginRedirect(loginRequest)
  }

  const loginWithPopup = async () => {
    if (!instance) {
      throw new Error('Azure SSO is not configured')
    }
    // Prevent calling interactive APIs while another interaction is in progress
    if (inProgress && inProgress !== InteractionStatus.None) {
      throw new Error('An authentication interaction is already in progress. Please wait and try again.')
    }

    // Popup flow as fallback
    return instance.loginPopup(loginRequest)
  }

  return {
    login,
    loginWithPopup,
    isConfigured: isAzureSSOConfigured(),
    isInteractionInProgress: Boolean(inProgress && inProgress !== InteractionStatus.None),
  }
}

/**
 * Hook to initiate SSO logout
 */
export const useSSOLogout = () => {
  const { instance, accounts } = useMsal()
  const dispatch = useDispatch<AppDispatch>()

  const logout = async () => {
    // Clear local state first
    dispatch(clearAuth())
    localStorage.removeItem('user')
    localStorage.removeItem('token')
    localStorage.removeItem('refresh_token')

    if (instance && accounts.length > 0) {
      // Clear MSAL cache and redirect to Azure AD logout
      await instance.logoutRedirect({
        account: accounts[0],
      })
    }
  }

  return { logout, isConfigured: isAzureSSOConfigured() }
}

export { msalInstance }
